import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type {
    IRelayEndpointResponseDto,
    IRelayGroupRegistrationFormDto,
    IRelayGroupRegistrationRequestDto,
} from '@experiences/interfaces';
import {
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import {
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    useFieldArray,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWR from 'swr';

import { notificationType } from '../../common/constants/Constant';
import { routePaths } from '../../common/constants/routePaths';
import { useUiSnackBar } from '../../common/hooks/useUiSnackBar';
import {
    createRelayGroup,
    getRelayGroupDetailsWithEndpoints,
    updateRelayGroup,
} from '../../services/relay/RelayService';
import { accountGlobalId } from '../../store/selectors';
import {
    buildEndpointUrl,
    parseEndpointUrl,
} from './relayEndpointUtil';

const DEFAULT_SCHEME = 'https';

const DEFAULT_VALUES = {
    name: '',
    description: '',
    endpoints: [
        {
            name: '',
            description: '',
            url: '',
            scheme: DEFAULT_SCHEME,
            host: '',
            healthCheckPath: '',
        },
    ],
};

const useRegisterRelayGroupViewModel = ({ type }: { type: 'add' | 'edit' }) => {

    const {
        tenantId, relayGroupId,
    } = useParams() as { tenantId: string; relayGroupId: string };
    const initialTenantIdRef = useRef(tenantId);
    const accountId = useSelector(accountGlobalId);
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();
    const setErrorMessage = useCentralErrorSetter();
    const { getErrorMessage } = useGetErrorInfo();
    const { createNotificationWithOptions } = useUiSnackBar();
    const EnableRelayTCP = useFeatureFlagValue(Features.EnableRelayTCP.name);

    const {
        register, control, handleSubmit, formState, setValue, reset,
    } = useForm<IRelayGroupRegistrationFormDto>({
        mode: 'onBlur',
        defaultValues: DEFAULT_VALUES,
    });

    const {
        fields, append, remove,
    } = useFieldArray({
        control,
        name: 'endpoints',
    });

    const isTenantStale = tenantId !== initialTenantIdRef.current;
    const relayGroupDetailsCacheKey = !isTenantStale && type === 'edit' && accountId && tenantId && relayGroupId
        ? [ 'relay-group-details', accountId, tenantId, relayGroupId, 'edit' ]
        : null;

    const {
        data: relayGroupData, isValidating: relayGroupLoading,
    } = useSWR(
        relayGroupDetailsCacheKey,
        () => getRelayGroupDetailsWithEndpoints(accountId, tenantId, relayGroupId),
    );

    useEffect(() => {
        if (type === 'edit' && relayGroupData) {
            reset({
                name: relayGroupData.name ?? '',
                description: relayGroupData.description ?? '',
                endpoints: relayGroupData.endpoints.map((endpoint: IRelayEndpointResponseDto) => {
                    const {
                        scheme, host,
                    } = parseEndpointUrl(endpoint.url);
                    return {
                        id: endpoint.id,
                        name: endpoint.name,
                        description: endpoint.description ?? '',
                        url: endpoint.url,
                        scheme,
                        host,
                        isExisting: true,
                        healthCheckPath: endpoint.healthCheckPath ?? '',
                    };
                }),
            });
        }
    }, [ type, relayGroupData, reset ]);

    useEffect(() => {
        if (tenantId !== initialTenantIdRef.current) {
            navigate(getRoute(routePaths.adminTenantRelay.replace(':tenantId', tenantId)));
        }
    }, [ tenantId, navigate, getRoute ]);

    const onCancel = useCallback(() => {
        if (type === 'add') {
            navigate(getRoute(routePaths.adminTenantRelay.replace(':tenantId', tenantId)));
        } else {
            navigate(getRoute(
                routePaths.adminTenantRelayDetails
                    .replace(':tenantId', tenantId)
                    .replace(':relayGroupId', relayGroupId)
            ));
        }
    }, [ type, tenantId, relayGroupId, getRoute, navigate ]);

    const onSubmit = useCallback(async (formData: IRelayGroupRegistrationFormDto) => {
        try {
            const data: IRelayGroupRegistrationRequestDto = {
                name: formData.name,
                description: formData.description,
                endpoints: formData.endpoints.map(ep => {
                    const url = EnableRelayTCP
                        ? buildEndpointUrl(ep.scheme ?? DEFAULT_SCHEME, ep.host ?? '')
                        : (ep.url ?? '');
                    const isTcp = ep.scheme === 'tcp';
                    return {
                        id: ep.id,
                        name: ep.name,
                        description: ep.description,
                        url,
                        isExisting: ep.isExisting,
                        healthCheckPath: isTcp ? '' : ep.healthCheckPath,
                    };
                }),
            };
            let resultGroupId = relayGroupId;
            if (type === 'add') {
                const result = await createRelayGroup(accountId, tenantId, data);
                resultGroupId = result.id;
                createNotificationWithOptions({
                    message: translate(
                        { id: 'CLIENT_RELAY_GROUP_CREATED_SUCCESSFULLY' },
                        { name: data.name },
                    ),
                    type: notificationType.SUCCESS,
                    title: translate({ id: 'CLIENT_RELAY_GROUP_CREATED' }),
                });
                navigate(getRoute(
                    routePaths.adminTenantRelaySetupInstructions
                        .replace(':tenantId', tenantId)
                        .replace(':relayGroupId', resultGroupId)
                        .replace(':type', 'add')
                ));
            } else {
                if (!formState.isDirty) {
                    navigate(getRoute(routePaths.adminTenantRelay.replace(':tenantId', tenantId)));
                    return;
                }

                await updateRelayGroup(accountId, tenantId, relayGroupId, data);

                createNotificationWithOptions({
                    message: translate(
                        { id: 'CLIENT_RELAY_GROUP_UPDATED_SUCCESSFULLY' },
                        { name: data.name },
                    ),
                    type: notificationType.SUCCESS,
                    title: translate({ id: 'CLIENT_RELAY_GROUP_UPDATED' }),
                });
                navigate(getRoute(
                    routePaths.adminTenantRelay
                        .replace(':tenantId', tenantId)
                ));
            }

        } catch (error) {
            setErrorMessage(await getErrorMessage(error));
        }
    }, [
        type,
        accountId,
        tenantId,
        relayGroupId,
        getRoute,
        navigate,
        createNotificationWithOptions,
        translate,
        setErrorMessage,
        getErrorMessage,
        EnableRelayTCP,
        formState.isDirty,
    ]);

    return {
        type,
        loading: relayGroupLoading,
        methods: {
            register,
            formState,
            handleSubmit: handleSubmit(onSubmit),
            setValue,
            control,
            onCancel,
        },
        fieldArray: {
            fields,
            append,
            remove,
        },
    };
};

export default useRegisterRelayGroupViewModel;
